<template>
  <modal-dialog
    ref="modalRef"
    heading="Are you still there?"
    :subheading="modalSubheading"
    heading-icon="clock"
    color="primary"
    :has-opener="false"
    @modal-has-closed="handleModalClose"
  >
    <template #cancel-action>
      <button
        class="btn btn-secondary"
        @click="handleLogout"
      >
        Log out
      </button>
    </template>
    <template #primary-action>
      <button
        class="btn btn-primary"
        :disabled="isExtendingSession"
        @click="handleExtendSession"
      >
        {{ isExtendingSession ? 'Extending...' : 'Extend Session' }}
      </button>
    </template>
  </modal-dialog>
</template>

<script setup>
import dayjs from 'dayjs';
import { useAuth0 } from '@auth0/auth0-vue';
import { ref, onUnmounted, watch, computed } from 'vue';
import { useTokenRefreshEvent } from '@/composables/useTokenRefreshEvent';
import { clearStickyFiltersStorage } from '@/composables/useFilterUrlSync.js';

import ModalDialog from '@/components/ui/modal/ModalDialog.vue';

const { isAuthenticated, idTokenClaims, getAccessTokenSilently, logout } = useAuth0();

const modalRef = ref(null);
const timeoutId = ref(null);
const countdownIntervalId = ref(null);
const isExtendingSession = ref(false);
const timeRemaining = ref(0);

const WARNING_THRESHOLD = 60;

const { emitTokenRefreshEvent } = useTokenRefreshEvent(() => {
  startTimeoutMonitoring();
});

const modalSubheading = computed(() => {
  return `Your session will expire in ${timeRemaining.value} seconds. Would you like to extend your session?`;
});

const calculateTimeRemaining = () => {
  if (!idTokenClaims.value?.exp) return 0;
  const now = dayjs();
  const expiryTime = dayjs.unix(idTokenClaims.value?.exp);
  return expiryTime.diff(now, 'second');
};

const startTimeoutMonitoring = () => {
  closeModal();
  clearTimeout(timeoutId.value);
  clearInterval(countdownIntervalId.value);

  const remaining = calculateTimeRemaining();

  if (isAuthenticated.value && remaining > 0 && remaining <= WARNING_THRESHOLD) {
    showModal();
  } else {
    const timeUntilWarning = remaining - WARNING_THRESHOLD;
    if (timeUntilWarning <= 0) {
      handleLogout();
    } else {
      timeoutId.value = setTimeout(() => {
        showModal();
      }, timeUntilWarning * 1000);
    }
  }
};

const showModal = () => {
  timeRemaining.value = calculateTimeRemaining();

  countdownIntervalId.value = setInterval(() => {
    timeRemaining.value = calculateTimeRemaining();

    if (timeRemaining.value <= 0 && modalRef.value?.isModalOpen) {
      clearInterval(countdownIntervalId.value);
      handleLogout();
    }
  }, 1000);

  modalRef.value?.showModal();
};

const closeModal = () => {
  if (modalRef.value?.isModalOpen) {
    modalRef.value?.closeModal();
  }
};

const handleExtendSession = async () => {
  try {
    isExtendingSession.value = true;
    await getAccessTokenSilently();
    isExtendingSession.value = false;
    startTimeoutMonitoring();
    emitTokenRefreshEvent();
    clearInterval(countdownIntervalId.value);
  } catch (error) {
    console.error('Failed to extend session:', error);
    handleLogout();
  } finally {
    isExtendingSession.value = false;
  }
};

const handleLogout = () => {
  clearStickyFiltersStorage();
  logout({ logoutParams: { returnTo: window.location.origin + '/auth/login' } });
};

const handleModalClose = () => {
  clearInterval(countdownIntervalId.value);
  if (timeRemaining.value <= 0) {
    handleLogout();
  }
};

watch(
  () => idTokenClaims.value?.exp,
  (newValue) => {
    if (newValue) {
      startTimeoutMonitoring();
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  clearTimeout(timeoutId.value);
  clearInterval(countdownIntervalId.value);
});
</script>
